import type { BannerAdProvider } from "@yjsgames/ad-ports";
import { resolveBannerSurface, type BannerSurfaceInput } from "./bannerSurfaceContext.js";

const CG_DIAG = (import.meta.env["VITE_WEB_AD_PROVIDER"] as string | undefined) === "crazygames";

function diag(message: string, data?: Record<string, unknown>): void {
  if (!CG_DIAG) return;
  console.info(`[CG-DIAG][BannerController] ${message}`, data ?? {});
}

export interface CrazyGamesBannerCreationInput {
  readonly isNative: boolean;
  readonly webAdProvider: "house" | "crazygames";
  readonly isDev: boolean;
}

export function shouldCreateCrazyGamesBannerController(
  input: CrazyGamesBannerCreationInput,
): boolean {
  return !input.isNative && (input.webAdProvider === "crazygames" || input.isDev);
}

/**
 * CrazyGames 배너 일원화 컨트롤러 — surface 판정 + pause/resume + Provider 멱등 구동.
 *
 * 웹(CrazyGames) 전용. 탭 전환 / 설정 모달 open·close / adFree 변경 시 `evaluate()`를 호출하면
 * `resolveBannerSurface` 순수 판정으로 show/remove + gameplayStop/gameplayStart를 결정한다.
 *
 * 모바일 LevelPlay 배너는 기존 `BannerController`를 사용한다(pause 없음).
 */
export interface CrazyGamesBannerControllerDeps {
  readonly provider: BannerAdProvider;
  /** 현재 surface 상태를 읽는 lazy getter. */
  readonly getInput: () => BannerSurfaceInput;
  /** CrazyGames SDK gameplayStop — 배너 노출 중 게임 루프 일시정지. */
  readonly gameplayStop: () => void;
  /** CrazyGames SDK gameplayStart — 배너 제거 후 게임 루프 재개. */
  readonly gameplayStart: () => void;
  /** Phaser 전투/타이머 진행 정지 — DOM/탭 UI는 계속 조작 가능해야 한다. */
  readonly pauseGameplay: () => void;
  /** Phaser 전투/타이머 진행 재개. */
  readonly resumeGameplay: () => void;
}

export class CrazyGamesBannerController {
  private currentlyShown = false;
  private pendingShow = false;
  private surfacePaused = false;
  private generation = 0;

  constructor(private readonly deps: CrazyGamesBannerControllerDeps) {}

  evaluate(): void {
    const input = this.deps.getInput();
    const result = resolveBannerSurface(input);
    diag("evaluate", {
      input,
      result,
      currentlyShown: this.currentlyShown,
      pendingShow: this.pendingShow,
      surfacePaused: this.surfacePaused,
      generation: this.generation,
    });
    if (result.action === "show") {
      this.pauseSurface();
      if (!this.currentlyShown && !this.pendingShow) {
        const generation = ++this.generation;
        this.pendingShow = true;
        void Promise.resolve()
          .then(() => this.deps.provider.showBanner())
          .then((shown) => {
            diag("showBanner resolved", { shown, generation, currentGeneration: this.generation });
            if (generation !== this.generation) {
              if (shown !== false) this.deps.provider.removeBanner();
              return;
            }
            this.pendingShow = false;
            if (shown === false) {
              this.resumeSurface();
              return;
            }
            this.currentlyShown = true;
          })
          .catch((error) => {
            if (generation === this.generation) {
              this.pendingShow = false;
              this.resumeSurface();
            }
            diag("showBanner rejected", { generation, currentGeneration: this.generation, error });
            console.warn("[CrazyGames] banner show failed", error);
          });
      }
    } else {
      diag("remove surface", { currentlyShown: this.currentlyShown, surfacePaused: this.surfacePaused });
      this.generation++;
      this.pendingShow = false;
      if (this.currentlyShown) {
        this.deps.provider.removeBanner();
        this.currentlyShown = false;
      }
      this.resumeSurface();
    }
  }

  private pauseSurface(): void {
    if (this.surfacePaused) return;
    this.surfacePaused = true;
    diag("pauseSurface", { generation: this.generation });
    this.deps.gameplayStop();
    this.deps.pauseGameplay();
  }

  private resumeSurface(): void {
    if (!this.surfacePaused) return;
    this.surfacePaused = false;
    diag("resumeSurface", { generation: this.generation });
    this.deps.resumeGameplay();
    this.deps.gameplayStart();
  }
}
